/**
 * useOfflineSync — Matches Xamarin Connectivity_ConnectivityChanged + PushDataToServer()
 *
 * Listens for online/offline transitions. When the browser comes back online:
 * 1. Reads the offline queue from localStorage
 * 2. Sends each pending task update individually (avoids StopOnFirstActionError batch halt)
 * 3. Treats "already complete/cancelled" errors as soft successes (removes from queue)
 * 4. Stops on hard errors, preserving unprocessed items for next retry
 * 5. Invalidates TanStack Query caches so the UI refreshes
 * 6. Shows success/error toast
 */

import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { updateTask as manhattanUpdateTask } from '@/service/ManhattanService';
import { buildUpdateTaskTransmissionHeader } from '@/service/taskService';
import { useAppSelector } from '@/store';

import { getOfflineUpdatedTaskList, clearOfflineQueue, setOfflineQueue } from './offlineTaskQueue';
import { dashboardQueryKeys } from './queries';

import type { UpdateTaskRequestModel } from '@/types';

/**
 * Matches Xamarin pattern: treat "already complete" or "already cancelled" errors
 * as soft successes so they don't block the rest of the queue.
 * Xamarin: errorMessage.Contains("Complete") || errorMessage.Contains("Cancelled")
 */
function isSoftFailure(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const msg = error.message.toLowerCase();
  return msg.includes('already complete') || msg.includes('already cancelled');
}

export function useOfflineSync(
  showToast: (
    msg: string,
    opts?: { type?: 'success' | 'error' | 'warning' | 'info'; duration?: number }
  ) => void
) {
  const { t } = useTranslation();
  const orgId = useAppSelector((state) => String(state.user.plant?.org_id ?? ''));
  const userId = useAppSelector(
    (state) => `${state.user.user?.country_code ?? ''}${state.user.user?.mobile_no ?? ''}`
  );
  const plantId = useAppSelector((state) => state.user.plant?._id ?? '');
  const sessionId = useAppSelector((state) => state.user.sessionId ?? '');
  const queryClient = useQueryClient();

  // Track whether sync is already in progress to avoid duplicate calls
  const isSyncing = useRef(false);

  /**
   * Push all offline queued task updates to the server, one at a time.
   *
   * Xamarin sends all items as a single batch (PushDataToServer), but that
   * causes a stuck queue when one item fails with "already complete" due to
   * StopOnFirstActionError='Y'. Processing individually fixes this by
   * skipping soft failures and preserving only hard-failed + unprocessed items.
   */
  const pushDataToServer = useCallback(async () => {
    if (isSyncing.current) return;

    const pendingTasks = getOfflineUpdatedTaskList();
    if (pendingTasks.length === 0) return;
    if (!orgId) return;

    isSyncing.current = true;
    let syncedCount = 0;

    try {
      for (const task of pendingTasks) {
        const request: UpdateTaskRequestModel = {
          Payload: {
            TransmissionHeader: buildUpdateTaskTransmissionHeader(),
            Body: {
              OrgId: orgId,
              StopOnFirstActionError: 'Y',
              UpdateTaskDetails: [task],
            },
          },
          TraceData: {
            User: userId,
            Plant: plantId,
            SessionId: sessionId,
          },
        };

        try {
          await manhattanUpdateTask(request);
          syncedCount++;
        } catch (err: unknown) {
          if (isSoftFailure(err)) {
            // Task already complete/cancelled on server — discard from queue
            syncedCount++;
            continue;
          }
          // Hard error — stop processing, keep this item + remaining in queue
          throw err;
        }
      }

      // All items processed successfully
      clearOfflineQueue();
      showToast(t('connectivity.offlineSyncSuccess'), { type: 'success' });
    } catch {
      // Partial sync: remove only processed items, keep the rest
      if (syncedCount > 0) {
        setOfflineQueue(pendingTasks.slice(syncedCount));
      }
      showToast(t('connectivity.offlineSyncFailed'), { type: 'error' });
    } finally {
      isSyncing.current = false;

      // Invalidate all dashboard queries to refresh lists with whatever synced
      void queryClient.invalidateQueries({ queryKey: dashboardQueryKeys.upcomingTasks(orgId) });
      void queryClient.invalidateQueries({
        queryKey: dashboardQueryKeys.myTasks(orgId, userId),
      });
      void queryClient.invalidateQueries({
        queryKey: dashboardQueryKeys.inProgressTask(orgId, userId),
      });
      void queryClient.invalidateQueries({
        queryKey: dashboardQueryKeys.completedTasks(orgId, userId),
      });
    }
  }, [orgId, userId, plantId, sessionId, queryClient, showToast, t]);

  // Listen for online event — matches Xamarin Connectivity_ConnectivityChanged
  useEffect(() => {
    const handleOnline = () => {
      void pushDataToServer();
    };

    window.addEventListener('online', handleOnline);

    // Also try to sync on mount if we're already online (handles page refresh while queued)
    if (navigator.onLine) {
      void pushDataToServer();
    }

    return () => {
      window.removeEventListener('online', handleOnline);
    };
  }, [pushDataToServer]);
}
